import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn, IconButton, Tooltip } from '@prenuvo/halo-foundation';
import { Duplicat } from '@prenuvo/halo-icon';

import { Logo } from '@/assets/icons';
import { CONFIG } from '@/core/config';
import { useUser } from '@/core/user';
import { PII_ROLE } from '@/core/user/role.constant';
import { FCT } from '@/i18n';
import { useStudyGuard } from '@/shared/guard';
import { formatTimestampToDate, transformSnakeCaseToPascalCase } from '@/shared/util';

import { extractSkuFromSkuId, getSkuPackage } from '../../../util';
import { ActivityLogsSidePanel } from './activity-logs-side-panel';
import { calculateAge, getPatientDisplayName, getPatientDob } from './header.util';
import { HeaderMenu } from './header-menu';
import { PatientContact } from './patient-contact';
import { PatientDocsSidePanel } from './patient-docs-side-panel';
import { ResetReportButton } from './reset-report-button.component';

export type HeaderProps = {
  className?: string;
};

export function Header({ className }: HeaderProps) {
  const {
    patient,
    radiologistScan: { machineID, scanDate, skuID },
    studyId,
  } = useStudyGuard();
  const { getRoles } = useUser();
  const isAusScanner = machineID?.startsWith('aus-');
  const hasPIIAccess = getRoles([PII_ROLE]) && isAusScanner;

  const [isActivityLogsSidePanelOpen, setIsActivityLogsSidePanelOpen] = useState(false);
  const [isPatientDocsOpen, setIsPatientsDocOpen] = useState(false);

  const patientPrenuvoID = patient.prenuvoID.replace('auth0|', '');
  const truncatedPatientID = `${patientPrenuvoID.slice(0, 6)}...`;
  const patientDisplayName = getPatientDisplayName(patient, hasPIIAccess);
  const patientDisplayDob = getPatientDob(patient, hasPIIAccess);
  const SKU_PACKAGE = getSkuPackage(extractSkuFromSkuId(skuID));

  const infoItem = (label: string, value?: string) => (
    <span key={label} className="flex border-r-2 px-2 py-1">
      {label.toLocaleLowerCase() !== 'package' && (
        <span data-testid={`patient-${label}`}>
          {label === 'DOB' && !hasPIIAccess ? `~ ${label}:` : `${label}:`}
        </span>
      )}
      <div className="px-1" data-testid={`patient-${label}-value`}>
        {value}
      </div>
    </span>
  );

  const staticInfoMapping = {
    DOB: FCT.REPORTING.HEADER.DOB(
      hasPIIAccess ? patient?.dob : patient?.fake_dob,
      calculateAge(patient, hasPIIAccess),
      hasPIIAccess,
    ),
    'Scan Date': formatTimestampToDate(scanDate),
  };

  const responsiveInfoMapping = {
    Identifier: studyId?.slice(0, 8),
    Package: SKU_PACKAGE,
    Scanner: machineID || FCT.REPORTING.HEADER.UNKNOWN.SCANNER,
  };

  const responsiveList = (
    <div className="flex flex-col space-y-2">
      {Object.entries(responsiveInfoMapping).map(([label, value]) => (
        <span key={label} className="flex px-2 py-1">
          <span data-testid={`header-${label}`}>{label !== 'Package' && `${label}:`}</span>
          <div className="pl-1" data-testid={`header-${label}-value`}>
            {value}
          </div>
        </span>
      ))}
    </div>
  );

  const getFormattedPatientSummary = () => {
    const summary = [
      patientDisplayName,
      `${patient?.gender ? transformSnakeCaseToPascalCase(patient?.gender) : FCT.REPORTING.HEADER.UNKNOWN.GENDER}`,
      `Prenuvo ID: ${patientPrenuvoID}`,
      patientDisplayDob,
      `Scan date: ${formatTimestampToDate(scanDate)}`,
      `Scanner: ${machineID || FCT.REPORTING.HEADER.UNKNOWN.SCANNER}`,
      `Identifier: ${studyId?.slice(0, 8)}`,
      `Package: ${SKU_PACKAGE}`,
    ]
      .filter(Boolean)
      .join('\n');

    return summary;
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(getFormattedPatientSummary());
  };

  const handlePatientDocsSidePanelOpenChange = (value: boolean) => {
    if (isActivityLogsSidePanelOpen) {
      setIsActivityLogsSidePanelOpen(false);
    }

    setIsPatientsDocOpen(value);
  };

  const handleActivityLogsSidePanelOpenChange = (value: boolean) => {
    if (isPatientDocsOpen) {
      setIsPatientsDocOpen(false);
    }

    setIsActivityLogsSidePanelOpen(value);
  };

  return (
    <>
      <div
        className={cn(
          'flex h-16 rounded-t-2xl bg-stone-900 dark:bg-stone-900 px-4 text-neutral-400 dark:text-neutral-400',
          className,
        )}
        data-testid="header-container"
      >
        <div className="flex items-center">
          <Tooltip content={FCT.REPORTING.HEADER.ACTIONS.RETURN_TO_DASHBOARD} side="bottom">
            <Link
              aria-label={FCT.REPORTING.HEADER.ACTIONS.RETURN_TO_DASHBOARD}
              to={`${CONFIG.APPS_SERVER}/radiologist/dashboard/`}
            >
              <img alt="Prenuvo Logo" data-testid="header-icon" src={Logo} />
            </Link>
          </Tooltip>
        </div>
        <div className="flex flex-1 items-center pl-2">
          <Tooltip content={FCT.REPORTING.HEADER.ACTIONS.COPY_STUDY_DETAILS} side="bottom">
            <IconButton
              aria-label={FCT.REPORTING.HEADER.ACTIONS.COPY_STUDY_DETAILS}
              data-testid="copy-patient-summary-button"
              icon={Duplicat}
              onClick={handleCopyToClipboard}
              size="sm"
              variant="text"
            />
          </Tooltip>
          <span className="flex items-center pl-1 text-sm leading-none">
            <span className="flex items-center py-1 pr-1" data-testid="patient-name">
              {patientDisplayName}
              <PatientContact />
            </span>
            <span className="border-x-2 px-2 py-1" data-testid="patient-gender">
              {patient?.gender
                ? transformSnakeCaseToPascalCase(patient?.gender)
                : FCT.REPORTING.HEADER.UNKNOWN.GENDER}
            </span>
            <Tooltip content={patientPrenuvoID} side="bottom">
              <span className="border-r-2 px-2 py-1" data-testid="patient-id">
                Prenuvo ID: {truncatedPatientID}
              </span>
            </Tooltip>
            {Object.entries(staticInfoMapping).map(([label, value]) => infoItem(label, value))}
            <div className="hidden 2xl:flex">
              {Object.entries(responsiveInfoMapping).map(([label, value]) =>
                infoItem(label, value),
              )}
            </div>
            <div className="flex cursor-pointer pl-2 2xl:hidden" data-testid="patient-info-tooltip">
              <Tooltip content={responsiveList} side="bottom">
                <span className="rounded-md bg-stone-800 px-2 py-1">
                  +{Object.entries(responsiveInfoMapping).length}
                </span>
              </Tooltip>
            </div>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ResetReportButton />

          <PatientDocsSidePanel
            isOpen={isPatientDocsOpen}
            onOpenChange={handlePatientDocsSidePanelOpenChange}
          />

          <HeaderMenu
            onOpenActivityLogs={() =>
              handleActivityLogsSidePanelOpenChange(!isActivityLogsSidePanelOpen)
            }
          />
        </div>
      </div>

      <ActivityLogsSidePanel
        isOpen={isActivityLogsSidePanelOpen}
        onClose={() => handleActivityLogsSidePanelOpenChange(false)}
      />
    </>
  );
}
